// FeedbackModal — direct lift of `tools/image-studio/FeedbackModal.jsx`.
//
// Removed the per-tool dropdown (Image Studio's `tool` field) since this
// modal lives inside one tool. Everything else — rating widget, low-rating
// comment requirement, category dropdown — matches Image Studio so users
// recognise the form. Submits to the Lambda's /api/feedback route.

import React, { useState } from 'react';
import {
  Modal,
  Box,
  Button,
  Alert,
  SpaceBetween,
  FormField,
  Select,
  Textarea,
} from '@cloudscape-design/components';

import { storeCROAuditApi } from './storeCROAuditApi.js';

const CATEGORY_OPTIONS = [
  { label: 'Score accuracy', value: 'score_accuracy' },
  { label: 'Audit took too long', value: 'performance' },
  { label: 'UI / workflow confusing', value: 'ux' },
  { label: 'Missing feature in scoring', value: 'missing_feature' },
  { label: 'Other', value: 'other' },
];

const LOW_RATING_THRESHOLD = 2;

export default function FeedbackModal({ visible, onDismiss, username }) {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [category, setCategory] = useState(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const reset = () => {
    setRating(0);
    setHoverRating(0);
    setCategory(null);
    setComment('');
    setSubmitting(false);
    setSubmitted(false);
    setError('');
  };

  const handleDismiss = () => {
    reset();
    onDismiss();
  };

  const commentRequired = rating > 0 && rating <= LOW_RATING_THRESHOLD;
  const canSubmit =
    rating > 0 &&
    (!commentRequired || comment.trim().length > 0) &&
    !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      await storeCROAuditApi.submitFeedback({
        rating,
        category: category?.value || '',
        comment: comment.trim(),
        user: username || 'unknown',
        page: typeof window !== 'undefined' ? window.location?.pathname : '',
      });
      setSubmitted(true);
      setTimeout(() => handleDismiss(), 1200);
    } catch (err) {
      setError(err.message || 'Could not submit feedback.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      visible={visible}
      onDismiss={handleDismiss}
      closeAriaLabel="Close feedback"
      header="💬 Share your feedback"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={handleDismiss} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={submitting}
              disabled={!canSubmit}
            >
              Submit
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        {submitted && <Alert type="success">Thanks — feedback received.</Alert>}
        {error && (
          <Alert type="error" dismissible onDismiss={() => setError('')}>
            {error}
          </Alert>
        )}

        <FormField label="How would you rate your experience?" description="Required.">
          <StarRating
            value={rating}
            hoverValue={hoverRating}
            onChange={setRating}
            onHover={setHoverRating}
            disabled={submitting}
          />
        </FormField>

        <FormField label="What was it about? (optional)">
          <Select
            selectedOption={category}
            onChange={({ detail }) => setCategory(detail.selectedOption)}
            options={CATEGORY_OPTIONS}
            placeholder="Pick a category"
            disabled={submitting}
          />
        </FormField>

        <FormField
          label="Comments"
          description={
            commentRequired
              ? 'Required for ratings of 2 or lower — tell us what went wrong.'
              : 'Anything specific you want us to know? (optional)'
          }
          errorText={
            commentRequired && comment.trim().length === 0
              ? 'Please add a short comment.'
              : undefined
          }
        >
          <Textarea
            value={comment}
            onChange={({ detail }) => setComment(detail.value)}
            placeholder={
              commentRequired
                ? 'e.g., The score for navlik.com seems too low'
                : 'e.g., Loved the per-store history view'
            }
            rows={3}
            disabled={submitting}
          />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
}

/** Same StarRating as `tools/image-studio/FeedbackModal.jsx`. */
function StarRating({ value, hoverValue, onChange, onHover, disabled }) {
  const stars = [1, 2, 3, 4, 5];
  const effective = hoverValue || value;
  return (
    <div
      role="radiogroup"
      aria-label="Rating"
      style={{ display: 'inline-flex', gap: 4 }}
      onMouseLeave={() => !disabled && onHover(0)}
    >
      {stars.map((n) => {
        const filled = n <= effective;
        return (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={value === n}
            aria-label={`${n} star${n === 1 ? '' : 's'}`}
            onClick={() => !disabled && onChange(n)}
            onMouseEnter={() => !disabled && onHover(n)}
            onFocus={() => !disabled && onHover(n)}
            onBlur={() => !disabled && onHover(0)}
            disabled={disabled}
            style={{
              background: 'none',
              border: 'none',
              cursor: disabled ? 'default' : 'pointer',
              fontSize: 28,
              lineHeight: 1,
              padding: '0 2px',
              color: filled ? '#f2c94c' : '#d1d5db',
              transition: 'color 0.12s',
            }}
          >
            {filled ? '★' : '☆'}
          </button>
        );
      })}
      {effective > 0 && (
        <Box fontSize="body-s" color="text-body-secondary" margin={{ left: 's' }}>
          {['', 'Poor', 'Fair', 'OK', 'Good', 'Great'][effective]}
        </Box>
      )}
    </div>
  );
}
